<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isVisible"
        ref="popoverRef"
        :class="['popver', customClass]"
        :style="popoverStyle"
        role="tooltip"
        @mouseenter="handlePopoverMouseEnter"
        @mouseleave="handlePopoverMouseLeave"
      >
        <slot />
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'topleft'
  | 'topright'
  | 'bottomleft'
  | 'bottomright'
  | 'lefttop'
  | 'leftbottom'
  | 'righttop'
  | 'rightbottom'
type Trigger = 'click' | 'hover' | 'focus' | 'manual'

interface Props {
  target: string
  placement?: Placement
  triggers?: Trigger | Trigger[]
  show?: boolean
  delay?: number | { show: number; hide: number }
  offset?: number
  customClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'top',
  triggers: 'click',
  delay: 50,
  offset: 8,
})

const emit = defineEmits<{
  (e: 'show'): void
  (e: 'shown'): void
  (e: 'hide'): void
  (e: 'hidden'): void
}>()

const popoverRef = ref<HTMLElement>()
const isVisible = ref(false)
const popoverStyle = ref({})
const actualPlacement = ref(props.placement)
let showTimeout: number
let hideTimeout: number
let targetElement: HTMLElement | null = null

const triggerList = computed(() => {
  return Array.isArray(props.triggers) ? props.triggers : [props.triggers]
})

const delayShow = computed(() => {
  return typeof props.delay === 'number' ? props.delay : props.delay?.show || 0
})

const delayHide = computed(() => {
  return typeof props.delay === 'number' ? props.delay : props.delay?.hide || 0
})

const getTargetElement = () => {
  if (!targetElement) {
    targetElement = document.getElementById(props.target)
  }
  return targetElement
}

const calculatePosition = () => {
  const trigger = getTargetElement()
  if (!trigger || !popoverRef.value) return

  const triggerRect = trigger.getBoundingClientRect()
  const popoverRect = popoverRef.value.getBoundingClientRect()
  const scrollX = window.scrollX
  const scrollY = window.scrollY

  let top = 0
  let left = 0
  let placement = props.placement

  // Calculate initial position
  switch (placement) {
    case 'top':
    case 'topleft':
    case 'topright':
      top = triggerRect.top + scrollY - popoverRect.height - props.offset
      if (placement === 'topleft') {
        left = triggerRect.left + scrollX
      } else if (placement === 'topright') {
        left = triggerRect.right + scrollX - popoverRect.width
      } else {
        left = triggerRect.left + scrollX + triggerRect.width / 2 - popoverRect.width / 2
      }
      break
    case 'bottom':
    case 'bottomleft':
    case 'bottomright':
      top = triggerRect.bottom + scrollY + props.offset
      if (placement === 'bottomleft') {
        left = triggerRect.left + scrollX
      } else if (placement === 'bottomright') {
        left = triggerRect.right + scrollX - popoverRect.width
      } else {
        left = triggerRect.left + scrollX + triggerRect.width / 2 - popoverRect.width / 2
      }
      break
    case 'left':
    case 'lefttop':
    case 'leftbottom':
      left = triggerRect.left + scrollX - popoverRect.width - props.offset
      if (placement === 'lefttop') {
        top = triggerRect.top + scrollY
      } else if (placement === 'leftbottom') {
        top = triggerRect.bottom + scrollY - popoverRect.height
      } else {
        top = triggerRect.top + scrollY + triggerRect.height / 2 - popoverRect.height / 2
      }
      break
    case 'right':
    case 'righttop':
    case 'rightbottom':
      left = triggerRect.right + scrollX + props.offset
      if (placement === 'righttop') {
        top = triggerRect.top + scrollY
      } else if (placement === 'rightbottom') {
        top = triggerRect.bottom + scrollY - popoverRect.height
      } else {
        top = triggerRect.top + scrollY + triggerRect.height / 2 - popoverRect.height / 2
      }
      break
  }

  // Simple boundary checks
  const mainPlacement = placement.replace(/left|right|top|bottom$/i, '') as Placement

  if (mainPlacement === 'top' && triggerRect.top < popoverRect.height + props.offset) {
    top = triggerRect.bottom + scrollY + props.offset
    actualPlacement.value = 'bottom'
  } else if (
    mainPlacement === 'bottom' &&
    window.innerHeight - triggerRect.bottom < popoverRect.height + props.offset
  ) {
    top = triggerRect.top + scrollY - popoverRect.height - props.offset
    actualPlacement.value = 'top'
  } else if (mainPlacement === 'left' && triggerRect.left < popoverRect.width + props.offset) {
    left = triggerRect.right + scrollX + props.offset
    actualPlacement.value = 'right'
  } else if (mainPlacement === 'right' && window.innerWidth - triggerRect.right < popoverRect.width + props.offset) {
    left = triggerRect.left + scrollX - popoverRect.width - props.offset
    actualPlacement.value = 'left'
  } else {
    actualPlacement.value = mainPlacement
  }

  // Constrain to viewport
  if (left < scrollX + 5) left = scrollX + 5
  if (left + popoverRect.width > scrollX + window.innerWidth - 5) {
    left = scrollX + window.innerWidth - popoverRect.width - 5
  }
  if (top < scrollY + 5) top = scrollY + 5

  popoverStyle.value = {
    position: 'absolute',
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 1060,
  }
}

const show = () => {
  clearTimeout(hideTimeout)
  if (isVisible.value) return

  showTimeout = window.setTimeout(async () => {
    emit('show')
    isVisible.value = true
    await nextTick()
    calculatePosition()
    emit('shown')
  }, delayShow.value)
}

const hide = () => {
  clearTimeout(showTimeout)
  if (!isVisible.value) return

  hideTimeout = window.setTimeout(() => {
    emit('hide')
    isVisible.value = false
    emit('hidden')
  }, delayHide.value)
}

const toggle = () => {
  isVisible.value ? hide() : show()
}

const handleTriggerClick = (e: Event) => {
  if (triggerList.value.includes('click')) {
    e.stopPropagation()
    toggle()
  }
}

const handleTriggerMouseEnter = () => {
  if (triggerList.value.includes('hover')) {
    show()
  }
}

const handleTriggerMouseLeave = () => {
  if (triggerList.value.includes('hover')) {
    hide()
  }
}

const handlePopoverMouseEnter = () => {
  if (triggerList.value.includes('hover')) {
    clearTimeout(hideTimeout)
  }
}

const handlePopoverMouseLeave = () => {
  if (triggerList.value.includes('hover')) {
    hide()
  }
}

const handleTriggerFocus = () => {
  if (triggerList.value.includes('focus')) {
    show()
  }
}

const handleTriggerBlur = () => {
  if (triggerList.value.includes('focus')) {
    hide()
  }
}

const setupTriggers = () => {
  const element = getTargetElement()
  if (!element) return

  if (triggerList.value.includes('click')) {
    element.addEventListener('click', handleTriggerClick)
  }
  if (triggerList.value.includes('hover')) {
    element.addEventListener('mouseenter', handleTriggerMouseEnter)
    element.addEventListener('mouseleave', handleTriggerMouseLeave)
  }
  if (triggerList.value.includes('focus')) {
    element.addEventListener('focus', handleTriggerFocus)
    element.addEventListener('blur', handleTriggerBlur)
  }
}

const cleanupTriggers = () => {
  const element = getTargetElement()
  if (!element) return

  element.removeEventListener('click', handleTriggerClick)
  element.removeEventListener('mouseenter', handleTriggerMouseEnter)
  element.removeEventListener('mouseleave', handleTriggerMouseLeave)
  element.removeEventListener('focus', handleTriggerFocus)
  element.removeEventListener('blur', handleTriggerBlur)
}

watch(
  () => props.show,
  (val) => {
    if (val !== undefined) {
      val ? show() : hide()
    }
  }
)

watch(isVisible, (val) => {
  if (val) {
    window.addEventListener('scroll', calculatePosition, true)
    window.addEventListener('resize', calculatePosition)
  } else {
    window.removeEventListener('scroll', calculatePosition, true)
    window.removeEventListener('resize', calculatePosition)
  }
})

onMounted(() => {
  setupTriggers()
  if (props.show) show()
})

onBeforeUnmount(() => {
  cleanupTriggers()
  clearTimeout(showTimeout)
  clearTimeout(hideTimeout)
  window.removeEventListener('scroll', calculatePosition, true)
  window.removeEventListener('resize', calculatePosition)
})

defineExpose({ show, hide, toggle })
</script>

<style lang="less" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s linear;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
